import { Flex } from "antd";
import { StoryboardMenu } from "./StoryboardMenu";
import { Chapter, TabKeys, TemplateType } from "../types";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "../hooks/useTemplateData";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import { PreviewSizes } from "../hooks/usePreviewResize";
import { SlideTemplatePreview } from "./Template/SlideTemplatePreview";
import { TemplatePreview } from "./Template/TemplatePreview";
import { EditableTemplate } from "./Template/EditableTemplate";
import { SlideEditableTemplate } from "./Template/SlideEditableTemplate";

import {
  getChapterHasTransition,
  getClosingEnabled,
  getIntroEnabled,
} from "../helpers";
import { ScrollObservedPreview } from "./ScrollObservedPreview";
import { ScrollActivateProvider } from "../hooks/useScrollActivate";
import { useScenesFit } from "../hooks/useScenesFit";
import { useMemo, useState } from "react";
import {
  ResizableScenesLayout,
  SlideSnapPage,
  StyledScrollableFlex,
  TemplateArea,
} from "./ResizableScenesLayout";
import { SNAP_PADDING_TOP } from "../constants";

export const StoryboardSlidesScenes = ({
  handleSlideCardClick,
  handleChapterCardClick,
  previewSizes,
}: {
  handleSlideCardClick: (uuid: string) => void;
  handleChapterCardClick: (chapter: Chapter) => void;
  previewSizes: PreviewSizes;
}) => {
  const {
    templatesData: { hasTransition },
    brandKitData: { intro, closing },
    storyboardSections,
    mediaGenerationSettingsData: { mediaGenerationSettings },
  } = useTemplateData();

  const {
    currentSlide,
    currentChapter,
    slideNameToTemplateIdMap,
    templates,
    currentItem,
    setCurrentItem,
  } = useTemplateEditorStore((store) => ({
    currentSlide: store.currentSlide,
    currentChapter: store.currentChapter,
    slideNameToTemplateIdMap: store.slideNameToTemplateIdMap,
    templates: store.templates,
    currentItem: store.templateState.currentItem,
    setCurrentItem: store.setCurrentItem,
  }));

  const setActiveTab = useTemplatesStatusStore((state) => state.setActiveTab);
  const zoom = useTemplatesStatusStore((state) => state.zoom);

  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);

  const { chapters } = mediaGenerationSettings ?? {};

  const showIntro =
    !!intro && getIntroEnabled(mediaGenerationSettings, !!intro);
  const showClosing =
    !!closing && getClosingEnabled(mediaGenerationSettings, !!closing);

  // Slides fill the scenes panel: fit the largest slide of the preview's aspect
  // ratio into the actual scroll viewport so it takes the full height (or full
  // width when width is the constraint). This computed size is the single
  // source of truth fed to the templates, toolbar and menu, keeping item
  // position math aligned.
  const { previewSize: fitSize, pageHeight } = useScenesFit(
    scrollEl,
    previewSizes
  );

  // Zoom is purely visual: scale the fitted preview size so the rendered scenes
  // grow/shrink together. Item position math keys off this same size, so the
  // relative values saved in the template are unaffected.
  const previewSize = useMemo(
    () => ({
      width: Math.round(fitSize.width * zoom),
      height: Math.round(fitSize.height * zoom),
    }),
    [fitSize.width, fitSize.height, zoom]
  );

  const templateSize = useMemo(
    () => ({
      width: previewSize.width,
      height: previewSize.height,
    }),
    [previewSize.width, previewSize.height]
  );

  const templatePreviewBorder = useMemo(
    () => ({
      borderWidth: 0,
      borderRadius: 0,
    }),
    []
  );

  const isCurrentIntro = currentChapter.type === TemplateType.INTRODUCTION;
  const isCurrentClosing = currentChapter.type === TemplateType.CLOSING;

  const introClosingCardProps = useMemo(
    () => ({
      previewSize,
      pageHeight,
      handleCardClick: handleChapterCardClick,
      templateSize,
      templatePreviewBorder,
    }),
    [
      previewSize,
      pageHeight,
      handleChapterCardClick,
      templateSize,
      templatePreviewBorder,
    ]
  );

  return (
    <ResizableScenesLayout
      menu={
        <StoryboardMenu
          previewWidth={previewSize.width}
          previewHeight={previewSize.height}
        />
      }
    >
      <StyledScrollableFlex
        ref={setScrollEl}
        className="video-editor-scene"
        vertical
        // Snap at <=100%: at and below original size a scene fits the viewport,
        // so the snap page is one viewport tall and snapping lands cleanly. Only
        // when zoomed in (>100%) is a scene taller than the viewport, leaving no
        // edge to snap to — there we let the user scroll freely.
        $snap={zoom <= 1}
        $snapPaddingTop={SNAP_PADDING_TOP}
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;

          // Check if the clicked element or any parent has data-interactive
          if (!target.closest("[data-interactive]") && !!currentItem) {
            setActiveTab(TabKeys.layout);
            setCurrentItem(undefined);
          }
        }}
      >
        <ScrollActivateProvider rootEl={scrollEl}>
          <Flex vertical>
            {showIntro && (
              <IntroClosingCard
                chapter={{
                  templateId:
                    mediaGenerationSettings?.introduction_template_id ??
                    undefined,
                  topic: "Introduction",
                  sectionId: "",
                  type: TemplateType.INTRODUCTION,
                }}
                isCurrent={isCurrentIntro}
                {...introClosingCardProps}
              />
            )}
            {storyboardSections.map((section) => {
              const chapterSettings = chapters?.find(
                (chapter) => chapter.chapter_id === section.id
              );
              const { transition_template_id } = chapterSettings ?? {};
              const chapterHasTransition = getChapterHasTransition(
                chapterSettings,
                hasTransition
              );

              return section.slides.map((slide) => {
                if (!slide.uuid) return;
                const slideUuid = slide.uuid;

                const template = templates.get(slideUuid);
                if (!template) return;

                const slideTemplateId = slideNameToTemplateIdMap[slideUuid];
                if (!slideTemplateId) return;

                const isTransitionSlide =
                  slideTemplateId === transition_template_id;
                if (isTransitionSlide && !chapterHasTransition) return;

                const isCurrentSlide =
                  !!currentSlide && currentSlide === slideUuid;

                const templatePreview = isCurrentSlide ? (
                  <SlideEditableTemplate
                    previewWidth={previewSize.width}
                    previewHeight={previewSize.height}
                    slide={slide}
                  />
                ) : (
                  <ScrollObservedPreview
                    onActivate={() => handleSlideCardClick(slideUuid)}
                  >
                    <SlideTemplatePreview
                      border={templatePreviewBorder}
                      templateSize={templateSize}
                      onCardClick={() => {
                        handleSlideCardClick(slideUuid);
                      }}
                      slide={slide}
                      template={template}
                    />
                  </ScrollObservedPreview>
                );

                return (
                  <SlideSnapPage key={slide.uuid} $minHeight={pageHeight}>
                    <TemplateArea>{templatePreview}</TemplateArea>
                  </SlideSnapPage>
                );
              });
            })}
            {showClosing && (
              <IntroClosingCard
                chapter={{
                  templateId:
                    mediaGenerationSettings?.closing_template_id ?? undefined,
                  topic: "Closing",
                  sectionId: "",
                  type: TemplateType.CLOSING,
                }}
                isCurrent={isCurrentClosing}
                {...introClosingCardProps}
              />
            )}
          </Flex>
        </ScrollActivateProvider>
      </StyledScrollableFlex>
    </ResizableScenesLayout>
  );
};

const IntroClosingCard = ({
  chapter,
  isCurrent,
  previewSize,
  pageHeight,
  handleCardClick,
  templateSize,
  templatePreviewBorder,
}: {
  chapter: Chapter;
  isCurrent: boolean;
  previewSize: { width: number; height: number };
  pageHeight: number;
  handleCardClick: (chapter: Chapter) => void;
  templateSize: { width: number; height: number };
  templatePreviewBorder: { borderWidth: number; borderRadius: number };
}) => {
  return (
    <SlideSnapPage $minHeight={pageHeight}>
      <TemplateArea>
        {isCurrent ? (
          <EditableTemplate
            previewWidth={previewSize.width}
            previewHeight={previewSize.height}
          />
        ) : (
          <ScrollObservedPreview onActivate={() => handleCardClick(chapter)}>
            <div style={{ marginBlockEnd: 18 }}>
              <TemplatePreview
                chapter={chapter}
                onCardClick={() => {
                  handleCardClick(chapter);
                }}
                templateSize={templateSize}
                border={templatePreviewBorder}
              />
            </div>
          </ScrollObservedPreview>
        )}
      </TemplateArea>
    </SlideSnapPage>
  );
};
